<template>
  <div>
    <nav class="wc-mobile-nav-v2">
      <router-link
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.SHOP }"
        :to="MOBILE_NAV_PATHS.SHOP"
      >
        <WCSvgIcon
          name="shop"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.6875, heightRem: 1.5625 }"
        />
        <span class="nav-tab__label">{{ $t('shop') }}</span>
      </router-link>

      <router-link
        v-if="showSaveCoupon"
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.REORDER }"
        :to="MOBILE_NAV_PATHS.REORDER"
      >
        <WCSvgIcon
          name="buy-it-again"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.4375, heightRem: 1.625 }"
        />
        <span class="nav-tab__label">{{ $t('reorder') }}</span>
      </router-link>
      <router-link
        v-else
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.LIST }"
        :to="MOBILE_NAV_PATHS.LISTS"
      >
        <WCSvgIcon
          name="shopping-lists"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.75, heightRem: 1.45831 }"
        />
        <span class="nav-tab__label">{{ $t('lists') }}</span>
      </router-link>

      <router-link
        class="nav-tab nav-tab--cart"
        :class="{ active: active === MOBILE_NAV_TABS.CART }"
        :to="MOBILE_NAV_PATHS.CART"
      >
        <div
          class="nav-cart-circle"
          :class="{
            'nav-cart-circle--active': active === MOBILE_NAV_TABS.CART,
            'nav-cart-circle--has-count': cartItemCount > 0,
          }"
        >
          <WCSvgIcon
            name="shopping-cart"
            class="nav-cart-circle__icon"
            :icon-size="{ widthRem: 2.4375, heightRem: 2.1875 }"
          />
          <span class="nav-cart-label" v-if="cartItemCount > 0">
            {{ cartItemCount }} {{ cartItemCount === 1 ? $t('item') : $t('items') }}
          </span>
        </div>
      </router-link>

      <router-link
        v-if="showSaveCoupon"
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.SAVE }"
        :to="MOBILE_NAV_PATHS.SAVE"
      >
        <WCSvgIcon
          name="save-coupon"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.75, heightRem: 1.25 }"
        />
        <span class="nav-tab__label">{{ $t('save') }}</span>
      </router-link>
      <router-link
        v-else
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.REORDER }"
        :to="MOBILE_NAV_PATHS.REORDER"
      >
        <WCSvgIcon
          name="buy-it-again"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.4375, heightRem: 1.625 }"
        />
        <span class="nav-tab__label">{{ $t('reorder') }}</span>
      </router-link>

      <router-link
        class="nav-tab"
        :class="{ active: active === MOBILE_NAV_TABS.SEARCH }"
        :to="MOBILE_NAV_PATHS.SEARCH"
      >
        <WCSvgIcon
          name="search-icon"
          class="nav-tab__icon"
          :icon-size="{ widthRem: 1.4375, heightRem: 1.4375 }"
        />
        <span class="nav-tab__label">{{ $t('search') }}</span>
      </router-link>
    </nav>
    <div class="wc-mobile-nav-v2-filler" />
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';
import CartItemControls from '@/modules/cart/mixins/CartControls/CartItemControls';
import {
  MOBILE_NAV_PATHS,
  MOBILE_NAV_TABS,
  MOBILE_NAV_PATH_TO_TAB,
} from '@/constants/AppConstants';

export default {
  mixins: [CartItemControls],

  data() {
    return {
      navResizeObserver: null,
      MOBILE_NAV_PATHS,
      MOBILE_NAV_TABS,
    };
  },

  mounted() {
    this.updateNavHeightCssVar();
    this.observeNavHeight();
  },

  unmounted() {
    this.disconnectNavHeightObserver();
    document.documentElement.style.setProperty('--mobile-nav-height', '0px');
  },

  methods: {
    updateNavHeightCssVar() {
      const nav = this.$el?.querySelector('.wc-mobile-nav-v2');
      const height = Math.ceil(nav?.getBoundingClientRect?.().height || nav?.offsetHeight || 0);
      document.documentElement.style.setProperty('--mobile-nav-height', `${height}px`);
    },
    observeNavHeight() {
      if (typeof ResizeObserver !== 'function' || !this.$el) return;
      this.disconnectNavHeightObserver();
      const nav = this.$el.querySelector('.wc-mobile-nav-v2');
      if (!nav) return;

      const resizeObserver = new ResizeObserver(() => {
        this.updateNavHeightCssVar();
      });

      if (!resizeObserver || typeof resizeObserver.observe !== 'function') return;

      this.navResizeObserver = markRaw(resizeObserver);
      this.navResizeObserver.observe(nav);
    },
    disconnectNavHeightObserver() {
      if (this.navResizeObserver && typeof this.navResizeObserver.disconnect === 'function') {
        this.navResizeObserver.disconnect();
      }
      this.navResizeObserver = null;
    },
  },

  computed: {
    ...mapGetters({
      carts: 'cart/nonEmptyCarts',
      cartsCount: 'cart/getCartsCount',
    }),

    active() {
      return MOBILE_NAV_PATH_TO_TAB[this.$route.path] || MOBILE_NAV_TABS.SHOP;
    },

    showSaveCoupon() {
      return this.$configuration.allowCouponClipping;
    },

    cartItemCount() {
      let count;
      if (this.$configuration.orderTypesEnabled && !this.isCustomerModeScan) {
        count = this.carts.reduce((sum, cart) => sum + this.cartLineItemCount(cart), 0);
      } else {
        count = this.cartLineItemCount();
      }
      return count;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-mobile-nav-v2 {
  position: fixed;
  bottom: env(safe-area-inset-bottom, 0);
  left: 0;
  width: 100%;
  height: convert-px-to-rem(80);
  z-index: 2999;
  background-color: var(--white);
  box-shadow: 0 -0.2rem 0.2rem rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: stretch;
  overflow: visible;
}

.wc-mobile-nav-v2-filler {
  position: fixed;
  bottom: 0;
  width: 100%;
  height: convert-px-to-rem(80);
  z-index: 2998;
  background-color: var(--white);
}

.nav-tab {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  font-size: $font-size-xs;
  text-decoration: none;
  color: var(--dark-lighter-3, get-color('dark', 'lighter-3'));

  &:hover {
    background-color: var(--gray-100, $gray-100);
  }

  &.active {
    color: var(--primary, $primary);

    .nav-tab__label {
      color: var(--primary, $primary);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: convert-px-to-rem(56);
      height: convert-px-to-rem(6);
      background-color: var(--primary, $primary);
      border-radius: convert-px-to-rem(100);
    }
  }
}

.nav-tab:not(.nav-tab--cart) {
  display: grid;
  grid-template-rows: convert-px-to-rem(28) auto;
  justify-items: center;
  align-content: center;
}

.nav-tab__icon {
  font-size: $font-size-xl;
  height: convert-px-to-rem(28);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-tab__label {
  margin-top: $pad-1;
  font-weight: 400;
  font-size: $font-size-base;
  line-height: normal;
  color: var(--dark-lighter-3, get-color('dark', 'lighter-3'));
}

.nav-tab--cart {
  position: relative;
  overflow: visible;
}

.nav-cart-circle--has-count {
  flex-direction: column;
  gap: convert-px-to-rem(2);
}

.nav-cart-label {
  font-size: $font-size-base;
  font-weight: 700;
  line-height: normal;
  color: var(--primary, $primary);
}

.nav-cart-circle {
  position: absolute;
  top: convert-px-to-rem(-20);
  left: 50%;
  transform: translateX(-50%);
  width: convert-px-to-rem(100);
  height: convert-px-to-rem(100);
  border-radius: 50%;
  background-color: var(--white);
  border: convert-px-to-rem(1) solid var(--gray-300, $gray-300);
  box-shadow: 0 convert-px-to-rem(-2) convert-px-to-rem(6) rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-cart-circle__icon {
  color: var(--primary, $primary);
}
</style>
